import { useEffect, useState } from "react";
import { Plus, Trash2, Save, CalendarOff, UserPlus, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionTitle, Card } from "../ui";
import DaySlotEditor from "./DaySlotEditor";

const FIELD = "bg-white border border-sand-200 rounded-lg px-2.5 py-1.5 text-sm text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50";
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function RangeRows({ ranges, onChange, testPrefix }) {
  return (
    <div className="flex flex-col gap-1.5">
      {ranges.map((r, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <input data-testid={`${testPrefix}-start-${i}`} type="time" className={`${FIELD} w-[110px]`} value={r.start}
            onChange={(e) => onChange(ranges.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} />
          <span className="text-charcoal-400 text-sm">–</span>
          <input data-testid={`${testPrefix}-end-${i}`} type="time" className={`${FIELD} w-[110px]`} value={r.end}
            onChange={(e) => onChange(ranges.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} />
          <button onClick={() => onChange(ranges.filter((_, j) => j !== i))} className="p-1 text-charcoal-400 hover:text-destructive"><Trash2 size={14} /></button>
        </div>
      ))}
      <button data-testid={`${testPrefix}-add`} onClick={() => onChange([...ranges, { start: "09:00", end: "17:00" }])}
        className="self-start rounded-full border border-sand-200 px-2.5 py-0.5 text-xs text-charcoal-600 hover:border-sage">+ hours</button>
    </div>
  );
}

function WeeklyHours({ emp, onChange }) {
  const wh = emp.weekly_hours || {};
  return (
    <div className="space-y-3">
      {DAY_NAMES.map((name, d) => {
        const key = String(d);
        const ranges = wh[key];
        const on = !!(ranges && ranges.length);
        return (
          <div key={d} className="flex items-start gap-3 border-b border-sand-100 pb-3 last:border-0">
            <label className="flex w-28 shrink-0 items-center gap-2 pt-1.5 text-sm text-charcoal-700">
              <input data-testid={`emp-day-toggle-${d}`} type="checkbox" checked={on}
                onChange={(e) => onChange({ ...wh, [key]: e.target.checked ? [{ start: "09:00", end: "17:00" }] : null })} />
              {name}
            </label>
            {on ? (
              <RangeRows ranges={ranges} testPrefix={`emp-day-${d}`} onChange={(r) => onChange({ ...wh, [key]: r })} />
            ) : (
              <p className="pt-1.5 text-sm text-charcoal-400">Unavailable</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DateOverrides({ emp, onChange }) {
  const [newDate, setNewDate] = useState("");
  const overrides = emp.date_overrides || {};
  return (
    <div>
      {Object.entries(overrides).sort().map(([date, ranges]) => (
        <div key={date} className="mb-3 rounded-xl bg-sand-50 p-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-charcoal-800">{date}</p>
            <div className="flex items-center gap-3">
              <label className="text-xs text-charcoal-600 inline-flex items-center gap-1.5">
                <input type="checkbox" checked={ranges.length === 0}
                  onChange={(e) => onChange({ ...overrides, [date]: e.target.checked ? [] : [{ start: "09:00", end: "17:00" }] })} />
                Unavailable all day
              </label>
              <button onClick={() => { const o = { ...overrides }; delete o[date]; onChange(o); }}
                className="p-1 text-charcoal-400 hover:text-destructive"><Trash2 size={14} /></button>
            </div>
          </div>
          {ranges.length > 0 && (
            <div className="mt-2"><RangeRows ranges={ranges} testPrefix={`ovr-${date}`} onChange={(r) => onChange({ ...overrides, [date]: r })} /></div>
          )}
        </div>
      ))}
      <div className="flex items-center gap-2">
        <input data-testid="override-date-input" type="date" className={FIELD} value={newDate} onChange={(e) => setNewDate(e.target.value)} />
        <button data-testid="override-add" onClick={() => { if (newDate) { onChange({ ...overrides, [newDate]: [{ start: "09:00", end: "17:00" }] }); setNewDate(""); } }}
          className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 px-3 py-1.5 text-xs text-charcoal-600 hover:border-sage"><Plus size={13} /> Add date</button>
      </div>
    </div>
  );
}

function BlockedTimes({ emp, onChange }) {
  const rows = emp.blocked_times || [];
  const edit = (i, k, v) => onChange(rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-2">
      {rows.map((b, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <input type="date" className={FIELD} value={b.date} onChange={(e) => edit(i, "date", e.target.value)} />
          <input type="time" className={FIELD} value={b.start} onChange={(e) => edit(i, "start", e.target.value)} />
          <span className="text-charcoal-400 text-sm">–</span>
          <input type="time" className={FIELD} value={b.end} onChange={(e) => edit(i, "end", e.target.value)} />
          <button onClick={() => onChange(rows.filter((_, j) => j !== i))} className="p-1 text-charcoal-400 hover:text-destructive"><Trash2 size={14} /></button>
        </div>
      ))}
      <button data-testid="blocked-time-add" onClick={() => onChange([...rows, { date: "", start: "12:00", end: "13:00" }])}
        className="rounded-full border border-sand-200 px-3 py-1 text-xs text-charcoal-600 hover:border-terracotta">+ Block a time</button>
    </div>
  );
}

function BlockedDates({ emp, onChange }) {
  const [newBlock, setNewBlock] = useState("");
  const dates = emp.blocked_dates || [];
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {dates.map((d) => (
          <span key={d} className="inline-flex items-center gap-1.5 rounded-full bg-clay-100 px-3 py-1 text-xs text-clay-800">
            {d}
            <button onClick={() => onChange(dates.filter((x) => x !== d))} className="hover:text-destructive">×</button>
          </span>
        ))}
        {dates.length === 0 && <p className="text-sm text-charcoal-400">No blocked days.</p>}
      </div>
      <div className="flex items-center gap-2">
        <input data-testid="block-date-input" type="date" className={FIELD} value={newBlock} onChange={(e) => setNewBlock(e.target.value)} />
        <button data-testid="block-add" onClick={() => { if (newBlock && !dates.includes(newBlock)) { onChange([...dates, newBlock]); setNewBlock(""); } }}
          className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 px-3 py-1.5 text-xs text-charcoal-600 hover:border-terracotta"><Plus size={13} /> Block day</button>
      </div>
    </div>
  );
}

export default function Availability() {
  const [employees, setEmployees] = useState([]);
  const [types, setTypes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [newEmp, setNewEmp] = useState({ name: "", email: "" });
  const [openCount, setOpenCount] = useState(null);
  const [saving, setSaving] = useState(false);

  const refreshCount = () => api.get("/availability/slots").then((r) => setOpenCount(r.data.slots.length)).catch(() => {});
  const loadEmployees = () => api.get("/admin/employees").then((r) => {
    setEmployees(r.data);
    setSelectedId((cur) => cur && r.data.some((e) => e.id === cur) ? cur : r.data[0]?.id || null);
  }).catch(() => setEmployees([]));
  const loadTypes = () => api.get("/admin/appointment-types").then((r) => setTypes(r.data)).catch(() => setTypes([]));

  useEffect(() => {
    loadEmployees();
    loadTypes();
    api.get("/admin/availability").then((r) => {
      const d = r.data || {};
      setSettings({
        timezone: d.timezone || "America/Mexico_City",
        slot_increment_minutes: d.slot_increment_minutes || 60,
        days_ahead: d.days_ahead || 28,
      });
    });
    refreshCount();
  }, []);

  const emp = employees.find((e) => e.id === selectedId);
  const editEmp = (k, v) => setEmployees((es) => es.map((e) => (e.id === selectedId ? { ...e, [k]: v } : e)));

  const addEmployee = async () => {
    if (!newEmp.name.trim()) return toast.error("Employee name is required");
    try {
      const { data } = await api.post("/admin/employees", newEmp);
      toast.success(`${data.name} added`);
      setNewEmp({ name: "", email: "" });
      await loadEmployees();
      setSelectedId(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add employee"); }
  };

  const saveEmployee = async () => {
    if (!emp) return;
    setSaving(true);
    try {
      await api.put(`/admin/employees/${emp.id}`, emp);
      await refreshCount();
      toast.success(`${emp.name}'s schedule saved`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const deleteEmployee = async () => {
    if (!emp || !window.confirm(`Delete ${emp.name}? Their upcoming assignments stay on existing bookings.`)) return;
    try {
      await api.delete(`/admin/employees/${emp.id}`);
      toast.success("Employee deleted");
      loadEmployees(); refreshCount();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const toggleAssign = async (type, empId) => {
    const has = (type.employee_ids || []).includes(empId);
    const ids = has ? (type.employee_ids || []).filter((x) => x !== empId) : [...(type.employee_ids || []), empId];
    setTypes((ts) => ts.map((t) => (t.id === type.id ? { ...t, employee_ids: ids } : t)));
    try {
      await api.put(`/admin/appointment-types/${type.id}`, { employee_ids: ids });
      refreshCount();
    } catch (e) { toast.error("Could not update assignment"); loadTypes(); }
  };

  const saveSettings = async () => {
    try {
      await api.put("/admin/availability", settings);
      await refreshCount();
      toast.success("Booking rules saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  if (!settings) return <p className="text-charcoal-500">Loading…</p>;

  return (
    <div data-testid="admin-availability">
      <SectionTitle title="Scheduling" subtitle="One place to manage your team, working hours, blocked time and which services each employee handles." />

      {openCount !== null && <p className="mb-5 text-sm text-charcoal-600"><b>{openCount}</b> open slots currently bookable</p>}

      {/* Team + assignments */}
      <Card className="mb-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-medium text-charcoal-800">Employees & service assignments</h3>
          <div className="flex flex-wrap items-center gap-2">
            <input data-testid="new-emp-name" className={`${FIELD} w-36`} placeholder="Name" value={newEmp.name} onChange={(e) => setNewEmp({ ...newEmp, name: e.target.value })} />
            <input data-testid="new-emp-email" className={`${FIELD} w-48`} placeholder="Email (optional)" value={newEmp.email} onChange={(e) => setNewEmp({ ...newEmp, email: e.target.value })} />
            <button data-testid="emp-add" onClick={addEmployee} className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-4 py-1.5 text-sm text-white font-medium hover:bg-terracotta-hover"><UserPlus size={14} /> Add</button>
          </div>
        </div>
        {employees.length === 0 ? (
          <p className="text-sm text-charcoal-500">No employees yet — add your first team member above.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-charcoal-500">
                  <th className="py-2 pr-4 font-medium">Appointment type</th>
                  {employees.map((e) => (
                    <th key={e.id} className="px-3 py-2 text-center font-medium">
                      <span className={e.active ? "text-charcoal-700" : "text-charcoal-300 line-through"}>{e.name}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {types.map((t) => (
                  <tr key={t.id} className="border-t border-sand-100">
                    <td className="py-2 pr-4 text-charcoal-800">
                      {t.name}
                      {!(t.employee_ids || []).length && <span className="ml-2 rounded-full bg-sage-light px-2 py-0.5 text-[11px] text-sage-700">everyone</span>}
                    </td>
                    {employees.map((e) => (
                      <td key={e.id} className="px-3 py-2 text-center">
                        <input data-testid={`assign-${t.id}-${e.id}`} type="checkbox"
                          checked={(t.employee_ids || []).includes(e.id)}
                          onChange={() => toggleAssign(t, e.id)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-charcoal-400">No boxes checked on a row = every active employee can take that appointment type.</p>
          </div>
        )}
      </Card>

      <DaySlotEditor employees={employees} types={types} onSaved={() => { loadEmployees(); refreshCount(); }} />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Employee selector + booking rules */}
        <div className="space-y-6">
          <Card>
            <h3 className="font-medium text-charcoal-800 mb-3">Team</h3>
            <div className="space-y-1.5">
              {employees.map((e) => (
                <button key={e.id} data-testid={`emp-select-${e.id}`} onClick={() => setSelectedId(e.id)}
                  className={`flex w-full items-center justify-between rounded-xl px-3.5 py-2.5 text-left text-sm transition-colors ${selectedId === e.id ? "bg-sage-light text-sage-700 font-medium" : "text-charcoal-700 hover:bg-sand-100"}`}>
                  <span>{e.name}</span>
                  {!e.active && <span className="rounded-full bg-sand-100 px-2 py-0.5 text-[11px] text-charcoal-400">inactive</span>}
                </button>
              ))}
            </div>
          </Card>

          <Card>
            <h3 className="flex items-center gap-2 font-medium text-charcoal-800 mb-4"><Clock size={15} /> Booking rules</h3>
            <div className="space-y-3">
              <label className="text-sm text-charcoal-600 block">Start time increments
                <select data-testid="availability-increment" className={`${FIELD} w-full mt-1`} value={settings.slot_increment_minutes} onChange={(e) => setSettings({ ...settings, slot_increment_minutes: Number(e.target.value) })}>
                  {[15, 30, 45, 60, 90, 120].map((n) => <option key={n} value={n}>{n} min</option>)}
                </select></label>
              <label className="text-sm text-charcoal-600 block">Bookable days ahead
                <input data-testid="availability-days-ahead" type="number" min={1} max={120} className={`${FIELD} w-full mt-1`} value={settings.days_ahead} onChange={(e) => setSettings({ ...settings, days_ahead: Number(e.target.value) })} /></label>
              <label className="text-sm text-charcoal-600 block">Timezone
                <input className={`${FIELD} w-full mt-1`} value={settings.timezone} onChange={(e) => setSettings({ ...settings, timezone: e.target.value })} /></label>
              <button data-testid="settings-save" onClick={saveSettings} className="w-full rounded-full bg-charcoal-800 px-4 py-2 text-sm text-white font-medium hover:bg-charcoal-900">Save rules</button>
            </div>
            <p className="mt-3 text-xs text-charcoal-400">Per-service rules (minimum notice, buffers, daily limits) live in each Appointment Type's Advanced settings.</p>
          </Card>
        </div>

        {/* Selected employee editor */}
        <div className="lg:col-span-2">
          {!emp ? (
            <Card><p className="text-charcoal-500">Add an employee to start setting hours.</p></Card>
          ) : (
            <Card>
              <div className="mb-5 space-y-3">
                <div className="grid gap-3 sm:grid-cols-3">
                  <input data-testid="emp-name" className={`${FIELD} w-full`} placeholder="Name" value={emp.name} onChange={(e) => editEmp("name", e.target.value)} />
                  <input data-testid="emp-email" className={`${FIELD} w-full`} placeholder="Email" value={emp.email || ""} onChange={(e) => editEmp("email", e.target.value)} />
                  <input data-testid="emp-phone" className={`${FIELD} w-full`} placeholder="WhatsApp phone" value={emp.phone || ""} onChange={(e) => editEmp("phone", e.target.value)} />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <label className="inline-flex items-center gap-2 text-sm text-charcoal-600">
                    <input data-testid="emp-active" type="checkbox" checked={!!emp.active} onChange={(e) => editEmp("active", e.target.checked)} /> Active
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button data-testid="emp-save" onClick={saveEmployee} disabled={saving}
                      className="inline-flex items-center gap-2 rounded-full bg-sage px-5 py-2 text-sm text-white font-medium hover:bg-sage-hover disabled:opacity-60">
                      <Save size={14} /> {saving ? "Saving…" : "Save schedule"}
                    </button>
                    <button data-testid="emp-delete" onClick={deleteEmployee} className="rounded-lg p-2 text-charcoal-400 hover:bg-sand-100 hover:text-destructive"><Trash2 size={16} /></button>
                  </div>
                </div>
              </div>

              <div className="grid gap-8 xl:grid-cols-2">
                <div>
                  <h4 className="font-medium text-charcoal-800 mb-3">Working hours</h4>
                  <WeeklyHours emp={emp} onChange={(wh) => editEmp("weekly_hours", wh)} />
                </div>
                <div className="space-y-8">
                  <div>
                    <h4 className="font-medium text-charcoal-800 mb-1">Date-specific hours</h4>
                    <p className="mb-3 text-xs text-charcoal-500">Override one date with different hours without changing the weekly pattern.</p>
                    <DateOverrides emp={emp} onChange={(o) => editEmp("date_overrides", o)} />
                  </div>
                  <div>
                    <h4 className="flex items-center gap-2 font-medium text-charcoal-800 mb-3"><CalendarOff size={15} className="text-terracotta" /> Blocked days</h4>
                    <BlockedDates emp={emp} onChange={(d) => editEmp("blocked_dates", d)} />
                  </div>
                  <div>
                    <h4 className="font-medium text-charcoal-800 mb-3">Blocked times</h4>
                    <BlockedTimes emp={emp} onChange={(b) => editEmp("blocked_times", b)} />
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
